import * as React from 'react'
import dynamic from 'next/dynamic'

const CloseIcon = dynamic(
  () => import('@thg-commerce/gravity-icons/src/components/Close'),
)
import { Button } from '@thg-commerce/gravity-elements'

import { facetsCallbacks } from '../../dataTracking'
import { SelectedFacets } from '../SelectedFacets/SelectedFacets'
import { Facet, FacetDataLayerSchema, FacetsProps } from '../types'
import { displayFacet } from '../utils'

import { MobileFacetSection } from './MobileFacetSection'
import {
  ClearAllWrapper,
  CloseButton,
  Content,
  FacetSectionsContainer,
  Header,
  MobileFacetsContainer,
  SaveButton,
  SaveWrapper,
  SelectedFacetsContainer,
  StyledHeading,
  StyledTitle,
} from './styles'

export interface MobileFacetsProps extends FacetsProps {
  showMobileFacets: boolean
  setShowMobileFacets: (state: boolean) => void
  i18nText: {
    close: string
    clearAll: string
    refine: string
    save: string
    back: string
    clear: string
    searchBrandLabel: string
    searchBrandPlaceholder: string
    minimumLabel: string
    maximumLabel: string
    sliderHeader: string
  }
  setRefreshFacets: (state: boolean) => void
  facetSchema: FacetDataLayerSchema
  enableAlternateMobileFacet?: boolean
  mobileFacetRefresh?: boolean
}

export const MobileFacets = React.memo((props: MobileFacetsProps) => {
  const selectedFacets = props.selectedFacets || []

  const facetsToDisplay = props.facets.map((facet) => {
    return (facet.__typename === Facet.SIMPLE ||
      facet.__typename === Facet.RANGED) &&
      displayFacet(facet.options) ? (
      <MobileFacetSection
        data-testid="mobile-facet-section"
        key={`mobilefacet-${facet.facetName}`}
        facet={facet}
        selectedFacets={selectedFacets}
        onInputChange={props.onInputChange}
        setShowMobileFacets={props.setShowMobileFacets}
        setRefreshFacets={props.setRefreshFacets}
        i18nText={{ ...props.i18nText }}
        facetSchema={props.facetSchema}
        enableAlternateMobileFacet={props.enableAlternateMobileFacet}
        mobileFacetRefresh={props.mobileFacetRefresh}
      />
    ) : (
      facet.__typename === Facet.SLIDER && (
        <MobileFacetSection
          data-testid="mobile-facet-section"
          key={`mobilefacet-${facet.facetName}`}
          facet={facet}
          selectedFacets={selectedFacets}
          onInputChange={props.onInputChange}
          setRefreshFacets={props.setRefreshFacets}
          setShowMobileFacets={props.setShowMobileFacets}
          i18nText={{
            ...props.i18nText,
          }}
          facetSchema={props.facetSchema}
          enableAlternateMobileFacet={props.enableAlternateMobileFacet}
          mobileFacetRefresh={props.mobileFacetRefresh}
        />
      )
    )
  })

  return (
    <MobileFacetsContainer
      showMobileFacets={props.showMobileFacets}
      enableAlternateMobileFacet={props.enableAlternateMobileFacet}
      data-testid="mobile-facets-container"
    >
      <Header enableAlternateMobileFacet={props.enableAlternateMobileFacet}>
        <CloseButton
          data-testid="mobile-facets-close-button"
          onClick={() => {
            props.setShowMobileFacets(false)
            facetsCallbacks.closeMobileFacetsClicked()
          }}
          enableAlternateMobileFacet={props.enableAlternateMobileFacet}
        >
          <CloseIcon />
          {!props.enableAlternateMobileFacet && props.i18nText.close}
        </CloseButton>
        {props.enableAlternateMobileFacet && (
          <StyledHeading>{props.i18nText.refine}</StyledHeading>
        )}
        {selectedFacets.length > 0 && (
          <ClearAllWrapper
            enableAlternateMobileFacet={props.enableAlternateMobileFacet}
          >
            <Button
              data-testid="mobile-facets-clear-all-button"
              emphasis="low"
              onClick={() => {
                props.onInputChange({
                  facets: [],
                })
                if (!props.mobileFacetRefresh) {
                  props.setRefreshFacets(true)
                }
                facetsCallbacks.clearAllClicked()
              }}
            >
              {props.i18nText.clearAll}
            </Button>
          </ClearAllWrapper>
        )}
      </Header>
      {!props.enableAlternateMobileFacet && (
        <StyledTitle>{props.i18nText.refine}</StyledTitle>
      )}
      <Content data-testid="mobile-facet-content">
        {selectedFacets.length > 0 && (
          <SelectedFacetsContainer
            enableAlternateMobileFacet={props.enableAlternateMobileFacet}
          >
            <SelectedFacets
              facets={props.facets}
              selectedFacets={props.selectedFacets}
              onInputChange={(facets) => {
                props.onInputChange(facets)
                if (!props.mobileFacetRefresh) {
                  props.setRefreshFacets(true)
                }
              }}
              i18nText={props.i18nText}
              enableAlternateMobileFacet={props.enableAlternateMobileFacet}
            />
          </SelectedFacetsContainer>
        )}
        <FacetSectionsContainer
          enableAlternateMobileFacet={props.enableAlternateMobileFacet}
        >
          {facetsToDisplay}
        </FacetSectionsContainer>
      </Content>
      <SaveWrapper
        enableAlternateMobileFacet={props.enableAlternateMobileFacet}
      >
        <SaveButton
          onClick={() => props.setShowMobileFacets(false)}
          data-testid="mobilefacets-save-button"
        >
          {props.i18nText.save}
        </SaveButton>
      </SaveWrapper>
    </MobileFacetsContainer>
  )
})
